import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Cta from '@/components/cta';
import { FAQ } from '@/components/faq';
import { Partners } from '@/components/partners';
import { useTranslation } from "react-i18next";
import { t } from 'i18next';
import AdvantagesSlider from './AdvantagesSlider';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import { HowItWorks } from '@/components/HowItWorks';
import { ICONS } from '@/assets/icons';

type Feature = {
  icon: any;
  title: string;
  desc: string;
};

type UseCase = {
  icon: any;
  title: string;
  description: string;
  image: string;
};

const advantages: Feature[] = [
  {
    icon: ICONS.upTo98PercentAlarmReduction,
    title: t('models.theftDetection.details.advantages.title1'),
    desc: t('models.theftDetection.details.advantages.description1')
  },
  {
    icon: ICONS.enhancedSituationalAwareness,
    title: t('models.theftDetection.details.advantages.title2'),
    desc: t('models.theftDetection.details.advantages.description2')
  },
  {
    icon: ICONS.seamlessIntegration,
    title: t('models.theftDetection.details.advantages.title3'),
    desc: t('models.theftDetection.details.advantages.description3')
  },
  {
    icon: ICONS.proactiveTheftPrevention,
    title: t('models.theftDetection.details.advantages.title4'),
    desc: t('models.theftDetection.details.advantages.description4')
  }
];



const useCases: UseCase[] = [
  {
    icon: ICONS.pharmacySecurity,
    title: t('models.theftDetection.details.useCase.title1'),
    description: t('models.theftDetection.details.useCase.description1'),
    image: './Shoplifting-detail/Pharmacy Security.jpg'
  },
  {
    icon: ICONS.bankSecurity,
    title: t('models.theftDetection.details.useCase.title2'),
    description: t('models.theftDetection.details.useCase.description2'),
    image: './Shoplifting-detail/Bank Security.jpg'
  },
  {
    icon: ICONS.schoolSecurity,
    title: t('models.theftDetection.details.useCase.title3'),
    description: t('models.theftDetection.details.useCase.description3'),
    image: './Shoplifting-detail/School Security.jpg'
  },
  {
    icon: ICONS.lakkSecurity,
    title: t('models.theftDetection.details.useCase.title4'),
    description: t('models.theftDetection.details.useCase.description4'),
    image: './Shoplifting-detail/Mall Security.jpg'
  }
];

const howItWorks = [
  {
    title: t('models.theftDetection.details.howItWorks.title1'),
    desc: t('models.theftDetection.details.howItWorks.description1'),
    icon: ICONS.analyseTempsReel
  },
  {
    title: t('models.theftDetection.details.howItWorks.title2'),
    desc: t('models.theftDetection.details.howItWorks.description2'),
    icon: ICONS.movementRecognition
  },
  {
    title: t('models.theftDetection.details.howItWorks.title3'),
    desc: t('models.theftDetection.details.howItWorks.description3'),
    icon: ICONS.alertsInstantanees
  },
];

({ service }: { service: Feature | UseCase }) => (
  <div className="group rounded-xl p-6 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:bg-slate-900 hover:text-white hover:shadow-xl relative overflow-hidden h-full flex flex-col">
    <div className="absolute top-0 left-0 w-full h-1 bg-slate-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl mb-4 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
      <FontAwesomeIcon icon={service.icon} />
    </div>
    <h4 className="text-lg font-bold mb-2">{service.title}</h4>
    <p className="text-sm opacity-90 flex-1">{'desc' in service ? service.desc : service.description}</p>
  </div>
);

const ShopliftModelDetails = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const [imgInView, setImgInView] = useState(false);
  const [stepInView, setStepInView] = useState([false, false, false]);

  // Alternating gradient helper
  const getSectionBg = (index: number) => {
    // Even: white â†’ slate-100 (light), slate-900 â†’ slate-800 (dark)
    // Odd:  slate-100 â†’ white (light), slate-800 â†’ slate-900 (dark)
    const light = index % 2 === 0 ? 'from-white to-slate-100' : 'from-slate-100 to-white';
    const dark = index % 2 === 0 ? 'dark:from-slate-900 dark:to-slate-800' : 'dark:from-slate-800 dark:to-slate-900';
    return `bg-gradient-to-b ${light} ${dark}`;
  };

  useEffect(() => {
    setImgInView(true);
    const timers = howItWorks.map((_, i) =>
      setTimeout(() => setStepInView((prev) => {
        const newState = [...prev];
        newState[i] = true;
        return newState;
      }), i * 300)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>

      <Header />
      {/* Hero Section */}

      <div id="hero" className="relative h-screen w-full overflow-hidden">
        {/* Background GIF with Overlay */}
        <div className="absolute inset-0 z-0">
          {/* Blue gradient overlays for cinematic darkening */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#001a33]/80 via-[#001a33]/60 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.35)_0%,_rgba(0,0,0,0.65)_100%)]" />
          <img
            src="./shoplifting.png"
            alt="Violence detection visual"
            className={`w-full h-auto object-cover rounded-xl shadow-2xl transition-all duration-700 ease-in-out ${imgInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto sm:mx-0 text-white px-6 sm:pl-6 sm:pr-0 md:pl-6 lg:pl-6 xl:pl-6 text-center sm:text-left">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide">
                <span className="block pt-3 sm:pt-4 md:pt-5 text-primary-200/90 tracking-wider">
                  {t('models.theftDetection.details.title')}
                </span>
              </h1>
              <h2 className="hidden sm:block mt-3 sm:mt-5 text-lg sm:text-2xl md:text-2xl font-korolev font-medium text-gray-100 max-w-2xl">
                {t('models.theftDetection.details.introduction', 'Découvrez comment notre technologie de détection de vol protège vos actifs en temps réel.')}
              </h2>


              <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 font-poppins">
                <Link href="/free-trial" className="group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-black hover:bg-gray-50 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-primary/20"
                  >
                    <span className="relative z-10">
                      {t('models.requestDemo')}
                    </span>
                  </Button>
                </Link>
                <Link
                  href="/contact-sales"
                  className="group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto bg-transparent border-white/70 text-white hover:bg-white/10 hover:border-white/90 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-white/10"
                  >
                    <span className="relative z-10">{t('models.contactUs')}</span>
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
          <button
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
            aria-label="Scroll down"
            className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
          >
            <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
              {t('hero.scrollDown', 'Scroll down')}
            </span>
            <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
              <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
            </div>
          </button>
        </div>
      </div>


      {/* Use Cases Section */}
      <section className={`py-20 ${getSectionBg(0)} text-slate-900 dark:text-white px-4 md:px-8 lg:px-16 relative overflow-hidden`}>        <div className="max-w-7xl mx-auto relative z-10">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-10 text-center tracking-tight">
          {t('models.theftDetection.details.useCase.title')}
        </h2>

        {/* Tabs row */}
        {/* Tabs row - no scrollbar, all tabs visible */}
        <div className="mb-10 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {useCases.map((useCase, i) => {
              const active = activeTab === i;
              return (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  aria-label={useCase.title}
                  className={`w-full flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl border transition-all
            ${active
                      ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                      : 'bg-white dark:bg-slate-900/50 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center
            ${active ? 'bg-white/10 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300'}`}>
                    <img
                      className={`w-8 h-8 ${active ? 'invert brightness-0' : ''} dark:invert dark:brightness-0`}
                      src={useCase.icon}
                      alt={useCase.title}
                    />
                  </span>
                  <span className={`text-sm sm:text-base font-semibold ${active ? 'text-white' : ''}`}>
                    {useCase.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content: text left, image right */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Left - Text */}
          <div className="order-2 md:order-1">
            <h3 className="text-2xl sm:text-3xl font-extrabold mb-4">
              {useCases[activeTab].title}
            </h3>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-base sm:text-lg max-w-xl">
              {useCases[activeTab].description}
            </p>
          </div>

          {/* Right - Image */}
          <div className="order-1 md:order-2">
            <img
              src={useCases[activeTab].image}
              alt={useCases[activeTab].title}
              className="w-full h-auto rounded-2xl shadow-lg object-cover"
              style={{ maxWidth: '100%' }}
            />
          </div>
        </div>
      </div>
      </section>

      {/* How It Works Section */}
      <HowItWorks
        title={t('models.fireSmokeDetection.details.howItWorks.title')}
        steps={howItWorks}
        stepInView={stepInView}
      />


      {/* Why Use Shoplifting Detection Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white px-4 md:px-8 lg:px-16 relative overflow-hidden">
        {/* Bokeh background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-400/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute top-32 right-20 w-24 h-24 bg-orange-400/20 rounded-full blur-lg animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-blue-300/15 rounded-full blur-2xl animate-pulse delay-2000"></div>
          <div className="absolute bottom-32 right-1/3 w-28 h-28 bg-orange-300/20 rounded-full blur-xl animate-pulse delay-500"></div>
          <div className="absolute top-1/2 right-10 w-36 h-36 bg-blue-500/10 rounded-full blur-2xl animate-pulse delay-1500"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 flex flex-col lg:flex-row gap-16 items-center">
          {/* Right side - Title and description */}
          <div className="order-1 lg:order-2 lg:w-2/5 flex flex-col justify-center">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-white leading-tight">
              {t('models.theftDetection.details.whyUse.title')}
            </h2>
            <p className="text-xl text-slate-200 leading-relaxed">
              {t('models.theftDetection.details.whyUse.description')}
            </p>
          </div>

          {/* Left side - Benefits boxes */}
          <div className="order-2 lg:order-1 lg:w-3/5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4, 5].map((idx) => {
              const isBlue = idx === 1 || idx === 4; // Cards 1 and 4 get blue, others get white
              const isLastCard = idx === 5;

              return (
                <div
                  key={idx}
                  className={`${isBlue
                      ? 'bg-slate-800/50 hover:bg-slate-800/80 border-blue-700/50'
                      : 'bg-white/20 hover:bg-white/30 border-white/30'
                    } backdrop-blur-sm rounded-xl border p-6 transition-all duration-300 hover:scale-105 ${isLastCard ? 'sm:col-span-2' : ''
                    }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${isBlue
                        ? 'bg-slate-700/80 text-white'
                        : 'bg-white/30 text-slate-800'
                      }`}>
                      {idx}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-2">
                        {t(`models.theftDetection.details.whyUse.point${idx}`)}
                      </h4>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {t(`models.theftDetection.details.whyUse.description${idx}`)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages and Limitations Section */}
      <section className="  py-12 sm:py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white px-2 sm:px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center tracking-tight bg-clip-text  bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300">
            {t("models.violenceDetection.details.advantages.title")}
          </h2>
          <div className="flex flex-col mt-8 sm:mt-16 lg:flex-row gap-10 lg:gap-16 items-stretch w-full">
            {/* Advantages Slider */}
            <div className="flex-1 flex flex-col min-w-0 max-w-full h-full">

              <div className="flex-1 flex flex-col h-full min-h-[260px]">
                <AdvantagesSlider items={advantages}
                  itemClassName="group flex items-center gap-4 py-4 px-6 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-300 min-h-[90px]"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      <Partners />
      <Cta />
      <div className="mt-20 mb-20 mx-5 md:mx-0">
        <FAQ faqs={t('models.theftDetection.details.faq', { returnObjects: true }) as Array<{ id?: string, question: string, answer: string }>} title={t('faq.title', 'Frequently Asked Questions')} subtitle={t('models.theftDetection.details.faqDescription', 'Everything you need to know about scalability')} />
      </div>
      <Footer />
    </>
  );
};

export default ShopliftModelDetails;